import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Palette, ShoppingBag } from "lucide-react";

const BUYER_TYPES = [
  { value: "retail", label: "Retail buyer" },
  { value: "collector", label: "Fine art collector" },
  { value: "business", label: "Business (hotel, gym, venue)" },
  { value: "designer", label: "Interior designer / Trade" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "buyer", buyer_type: "retail" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Account created");
      navigate(u.role === "artist" ? "/artist" : "/buyer");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/buyer";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif text-3xl">ARTFUTUR<span className="text-[var(--gold)]">.</span></Link>
          <h1 className="font-serif text-3xl mt-8">Create your account</h1>
          <p className="text-[var(--text-muted)] mt-1 text-sm">Join a global peer-to-peer art marketplace.</p>

          <button onClick={googleLogin} type="button" data-testid="google-register-btn"
            className="mt-6 w-full flex items-center justify-center gap-3 border border-[var(--border-subtle)] rounded-full py-3 text-sm font-medium hover:border-[var(--gold)] transition-colors">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
          </button>
          <div className="flex items-center gap-4 my-5">
            <div className="flex-1 h-px bg-[var(--border-subtle)]" />
            <span className="text-xs text-[var(--text-muted)] font-mono">OR SIGN UP WITH EMAIL</span>
            <div className="flex-1 h-px bg-[var(--border-subtle)]" />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            {[{ v: "buyer", l: "I'm a buyer", icon: ShoppingBag }, { v: "artist", l: "I'm an artist", icon: Palette }].map((r) => {
              const Icon = r.icon;
              const active = form.role === r.v;
              return (
                <button key={r.v} type="button" onClick={() => set("role", r.v)}
                  data-testid={`register-role-${r.v}`}
                  className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                    active ? "border-[var(--gold)] bg-[var(--gold-light)]" : "border-[var(--border-subtle)]"}`}>
                  <Icon className="w-4 h-4" /> {r.l}
                </button>
              );
            })}
          </div>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <label className="text-sm font-medium">Full name</label>
              <input required value={form.name} onChange={(e) => set("name", e.target.value)}
                data-testid="register-name-input"
                className="mt-1.5 w-full rounded-lg border border-[var(--border-subtle)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Email</label>
                <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                  data-testid="register-email-input"
                  className="mt-1.5 w-full rounded-lg border border-[var(--border-subtle)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)]" />
              </div>
              <div>
                <label className="text-sm font-medium">Mobile phone</label>
                <input required value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  placeholder="+33 6 00 00 00 00" data-testid="register-phone-input"
                  className="mt-1.5 w-full rounded-lg border border-[var(--border-subtle)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)]" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)}
                data-testid="register-password-input"
                className="mt-1.5 w-full rounded-lg border border-[var(--border-subtle)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)]" />
            </div>
            {form.role === "buyer" && (
              <div>
                <label className="text-sm font-medium">Buyer type</label>
                <select value={form.buyer_type} onChange={(e) => set("buyer_type", e.target.value)}
                  data-testid="register-buyertype-select"
                  className="mt-1.5 w-full rounded-lg border border-[var(--border-subtle)] px-4 py-3 text-sm bg-white focus:outline-none focus:border-[var(--gold)]">
                  {BUYER_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </div>
            )}
            <button type="submit" disabled={loading} data-testid="register-submit-btn"
              className="btn-gold w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
            </button>
          </form>

          <p className="text-sm text-[var(--text-muted)] mt-6 text-center">
            Already have an account? <Link to="/login" className="text-[var(--gold)] font-medium">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative grain order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1758972581195-c27b9d3b1c56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Gallery interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[var(--bg-dark)]/40" />
      </div>
    </div>
  );
}
